import {
	createRouterContext,
	type RouteResource,
	useResource,
	type Route,
	type UseResourceHookResponse,
} from '@atlassian/react-resource-router';
import type { RouteParamsWithProjectKey, UseResourceCustomContext } from '../types.tsx';
import { usePrefetchableResource } from '../use-init-fetch-resource/index.tsx';

/**
 * DUMMY_ROUTE needs to be used by default, because `route` must not be undefined, it is required/used internally by RRR, e.g. in findRouterContext() on createRouterContext() call.
 * If `route` is undefined, those will throw runtime exceptions.
 * `project-sidebar-navigation-resource/software-cmp-nav-synchronizer/index.tsx` uses dummy route as well.
 *
 * Jfyi, createProjectRouteRouterContext() function in JSW uses dummy route, with :projectKey in its path (that resource fetch function uses)
 */
const DUMMY_ROUTE: Route = {
	name: 'DUMMY_ROUTE_NAME',
	path: '/projects/:projectKey/dummyroute',
};

type UseResourceWithCustomRouterContextOptions = {
	/** If `true`, the provided resource will be requested once via `refresh()` when the component mounts. */
	shouldPrefetch?: boolean;
};

/**
 * Allows for router resources to be to be fetched on a route that doesn't include the required router params. A custom router context can be passed to the resource to fetch the data.
 * The resource will be automatically fetched via `refresh()` when the component mounts, unless disabled via `options.autoFetch = false`. If you don't need this behaviour, use `useResource` instead.
 *
 * Note: DACI about why this was introduced, if you're curious (this is the implementation of the Chosen option): https://hello.atlassian.net/wiki/spaces/JNR/pages/3718159933/DACI+-+How+should+we+fetch+side+navigation+data+for+multiple+projects
 */
export function useResourceWithCustomRouterContext<
	RouteResourceData,
	RouteParams = RouteParamsWithProjectKey,
>(
	resource: RouteResource<RouteResourceData>,
	customRouterContext: UseResourceCustomContext<RouteParams> | undefined,
	options: UseResourceWithCustomRouterContextOptions = {},
): UseResourceHookResponse<RouteResourceData> {
	let resourceOptions;

	if (customRouterContext) {
		// NOTE: `route` must include /.../:paramKey/... in its url for all `matchParams` passed to the `createRouterContext`, otherwise they will be ignored. createRouterContext will throw an error if route is undefined: `route.path` is used in the library code at least.
		const { route = DUMMY_ROUTE, matchParams } = customRouterContext;

		if (matchParams && Object.keys(matchParams).length > 0) {
			resourceOptions = {
				routerContext: createRouterContext(route, { params: matchParams }),
			};
		}
	}

	const { shouldPrefetch } = options;
	const resourceResponse = useResource<RouteResourceData>(resource, resourceOptions);

	// we must call "fetch data" / refresh() manually, because react-resource-router will not fetch resources which are not defined in the current route's required resources.
	usePrefetchableResource(resourceResponse, shouldPrefetch ?? Boolean(customRouterContext));

	return resourceResponse;
}
